<ng-container *ngIf="shippingDetails">
    <div class="row" *ngIf="showPostalCodeForm">
        <div class="col-sm-12">
            <h6><strong>Calculate your Shipping</strong></h6>
        </div>
        <div class="col-sm-12">
            <span>Enter your postal or zip code here to see your shipping rate.</span>
        </div>
        <div class="col-sm-12 pt-3 pb-3">
            <form [formGroup]="postalCodeForm" (ngSubmit)="getNewShippingQuote.emit()">
                <div class="w-100 input-group">
                    <div class="input-group-prepend">
                        <input 
                            type="submit"
                            value="CALCULATE"
                            class="btn btn-md btn-outline-primary m-0 px-3 py-2 z-depth-0 waves-effect"
                            [disabled]="shippingDetails.ConsistsEntirelyOfDigitalItems"
                            data-testid="calculate-shipping-button"
                        >
                    </div>
                    <input 
                        formControlName="postalCode"
                        type="text"
                        placeholder="Postal or Zip Code"
                        class="form-control"
                        mdbInput
                        mdbValidate
                        [attr.disabled]="shippingDetails.ConsistsEntirelyOfDigitalItems ? 'disabled' : null"
                        data-testid="postal-code-input"
                    >
                </div>
                
                <small class="text-danger" 
                    *ngIf="(postalCodeForm.touched || postalCodeForm.dirty) && postalCodeForm.get('postalCode').invalid && !shippingDetails.ConsistsEntirelyOfDigitalItems"
                    data-testid="postal-code-error"
                >
                    Enter a valid Post or Zip Code.
                </small>
            </form>
        </div>
    </div>

    <div class="row" *ngIf="consolidatedShippingRates.length > 0" data-testid="shipping-widget-rates-container">
        <div [ngClass]="{'col-md-6': !isCartPage, 'col-md-12': isCartPage }">
            <h6><strong>Choose Shipping Method</strong></h6>
            <form [formGroup]="ratesForm" data-testid="shipping-rates-form">
                <div class="row">
                    <div class="col-sm-12"
                        *ngFor="let rate of consolidatedShippingRates; index as i"
                        [ngClass]="{ 
                            'mb-3': !isCartPage,
                            'cart-shipping-margin cart-shipping-padding border-bottom': isCartPage
                        }"
                    >
                        <div class="d-flex justify-content-between">
                            <div>
                                <input
                                    class="form-check-input shipping-service-radio"
                                    type="radio"
                                    formControlName="selectedConsolidatedShippingRateId"
                                    [value]="rate.ConsolidatedShippingRateId"
                                    [id]="rate.ConsolidatedShippingRateId"
                                    attr.data-testid="shipping-rate-{{i}}-checkbox"
                                />
                                <label 
                                    style="margin: 0" 
                                   [for]="rate.ConsolidatedShippingRateId" 
                                    attr.data-testid="shipping-rate-{{i}}-label"
                                >
                                    {{ getFriendlyName(rate) }}
                                </label><br />
                                
                                <small
                                    class="badge green shipping-pill" 
                                    *ngIf="rate.EstimatedDeliveryDays && unableToImmediatelyShipItems.length == 0"
                                >
                                    ETA: {{ rate.EstimatedDeliveryDays }} Days
                                </small>
                                
                                <small
                                    *ngIf="!rate.EstimatedDeliveryDays || unableToImmediatelyShipItems.length > 0"
                                    class="badge orange hand shipping-pill"
                                    triggers="hover"
                                    placement="top"
                                    container="body"
                                    mdbPopoverHeader="Unable to determine estimated delivery speed."
                                    mdbPopover="Not all items in your order are in stock. Therefore, we are unable to estimate the delivery speed for this order."
                                >
                                    ETA Unavailable <i class="fas fa-info-circle"></i>
                                </small>

                                <small
                                    *ngIf="rate.Metadata"
                                    class="badge orange hand shipping-pill"
                                    triggers="hover"
                                    placement="top"
                                    container="body"
                                    mdbPopoverHeader="Metadata"
                                    bodyClass="shipping-rate-metadata"
                                    [mdbPopover]="rate.Metadata"
                                >
                                    Metadata
                                </small>
                            </div>
                            <span attr.data-testid="shipping-rate-{{i}}-price" class="shipping-rate-price">
                                {{ rate.CustomerShippingCost | currency }}
                            </span>
                        </div>
                    </div>

                    <div class="col-sm-12"
                        [ngClass]="{ 
                            'mb-3': !isCartPage, 
                            'cart-shipping-margin cart-shipping-padding': isCartPage
                        }"
                    >
                        <div class="d-flex justify-content-between">
                            <mdb-checkbox 
                                formControlName="requestInsurance" 
                                [id]="requestInsuranceId" 
                                [disabled]="!isEligibleForInsurance"
                                data-testid="request-insurance-checkbox"
                            >
                            </mdb-checkbox>
                            <label [for]="requestInsuranceId" class="request-insurance-row" data-testid="request-insurance-label">
                                <span>Package Protection</span><br />
                                <ng-container *ngIf="isEligibleForInsurance">
                                    <small>Protect your valuables during shipping.</small><br />
                                    <small><a href="/package-protection">Terms and Conditions</a></small>
                                </ng-container>
                                <small 
                                    *ngIf="!isEligibleForInsurance" 
                                    data-testid="insurance-not-eligible-error"
                                >
                                    {{ shippingDetails.InsuranceError }}<br/><br/>
                                    Additional info in the <a href="/package-protection">Terms and Conditions</a>.
                                </small>
                            </label>
                            <span *ngIf="isEligibleForInsurance" data-testid="insurance-cost-label">
                                {{ insuranceCost | currency }}
                            </span>
                        </div>
                    </div>
                </div>
            </form>
        </div>

        <div class="col-md-6" *ngIf="shippingAddress && pageScope === 'checkout'">
            <div class="row">
                <div class="col-sm-12">
                    <h6><strong>Shipping to:</strong></h6>
                </div>

                <div class="shipping-label ml-3 mr-3" data-testid="selected-shipping-address">
                    <div class="row">
                        <div class="col-sm-12">{{ shippingAddress.Address }}</div>
                        <div class="col-sm-12" *ngIf="shippingAddress.Address2?.trim() ?? ''">{{
                            shippingAddress.Address2 }}</div>
                        <div class="col-sm-12">{{ shippingAddress.City }}, {{ shippingAddress.ProvinceName }}
                        </div>
                        <div class="col-sm-12">{{ postCode }}</div>
                        <div class="col-sm-12">{{ shippingAddress.CountryName }}</div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</ng-container>